/**
 * Position report + close/rollover — uses shared session/CSV/form/date helpers (utils/*).
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { api } from "../../utils/api";
import { downloadCsvFromObjects } from "utils/csvExport";
import { extractSelectOrPrimitive } from "utils/formValues";
import { safeParseInt } from "utils/numberParse";
import { getNormalizedThunkSession } from "utils/tradeSession";
import { formatApiDateMomentIso } from "utils/dateFormats";
import { axiosRejectPayload, legacyAxiosToastMessage } from "utils/httpErrors";
import { mapOpenPositionRecords, mergeLiveMtmIntoOpenPositions } from "utils/openPositionMapper";
import { getMarketWatchList } from "./marketwatch.slice";
import { showMessage } from "./common.slice";
import { isSessionTradingAllowed } from "utils/sessionRole";
import { getSessionData } from "utils/common";

function pickCloseField(...vals) {
  for (const v of vals) {
    const s = v == null ? "" : String(v).trim();
    if (s) return s;
  }
  return "";
}

function findMarketWatchForPosition(marketWatchList, data) {
  if (!data || !marketWatchList) return null;
  const keys = [
    data.displayName,
    data.DisplayName,
    data.script,
    data.Script,
    data.symbol,
    data.Symbol,
    data.token,
    data.Token,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const tryGet = (k) => {
    if (!k || typeof marketWatchList.get !== "function") return null;
    if (marketWatchList.has(k)) return marketWatchList.get(k);
    return null;
  };

  for (const k of keys) {
    const hit = tryGet(k);
    if (hit) return hit;
  }

  const upper = new Set(keys.map((k) => k.toUpperCase()));
  const values =
    typeof marketWatchList.values === "function"
      ? marketWatchList.values()
      : Array.isArray(marketWatchList)
        ? marketWatchList
        : [];
  for (const v of values) {
    if (!v || typeof v !== "object") continue;
    const hay = [
      v.displayName,
      v.DisplayName,
      v.script,
      v.Script,
      v.symbol,
      v.Symbol,
      v.token,
      v.Token,
    ]
      .map((x) => String(x || "").trim().toUpperCase())
      .filter(Boolean);
    if (hay.some((h) => upper.has(h))) return v;
  }
  return null;
}

function closeExecutionType(data) {
  if (
    data.marketType == "market" ||
    data.marketType === 5 ||
    Number(data.executionType) === 5
  ) {
    return 5;
  }
  if (data.marketType == "limit" && Number(data.oldNetQty) > 0) return 3;
  if (data.marketType == "limit") return 1;
  return 5;
}

function buildClosePositionPayload(data, session, marketWatchList) {
  const mw = findMarketWatchForPosition(marketWatchList, data);
  // Lock/close matches Positions.Script — keep the Trade-row script.
  // GetPriceFromAPI matches Scripts.ScriptsFeederName — use market-watch displayName.
  const script = pickCloseField(data.script, data.Script, data.symbol, data.Symbol);
  const symbol = pickCloseField(data.symbol, data.Symbol, script);
  const displayName = pickCloseField(
    mw?.displayName,
    mw?.DisplayName,
    data.displayName,
    data.DisplayName,
    script,
    symbol
  );
  const rowUserId = safeParseInt(data.userId ?? data.UserId);
  const userId = rowUserId || safeParseInt(session.userId);
  const segmentId = safeParseInt(data.segmentId ?? data.SegmentId);
  const lot = Math.abs(Number(data.lot) || 0);
  const qtyRaw = Math.abs(Number(data.qty) || 0);
  const oneQtySize = Number(data.oneQtySize);
  const qty =
    segmentId !== 1 && Number.isFinite(oneQtySize) && oneQtySize > 0
      ? oneQtySize * lot
      : qtyRaw;
  const ltp = Number(
    data.price ?? data.ltp ?? mw?.lastTradedPrice ?? mw?.ltp ?? 0
  );
  const rawPositionId = Number(data.positionId ?? data.PositionId);
  const positionId = Number.isFinite(rawPositionId) && rawPositionId > 0 ? rawPositionId : null;
  return {
    createdBy: safeParseInt(session.userId) || userId,
    userId,
    id: 0,
    deviceType: 1,
    segmentId,
    script,
    displayName,
    userIp: session.userIp || session.userIpFinal || "",
    orderType: Number(data.oldNetQty) > 0 ? 2 : 1,
    lot,
    qty,
    rate: ltp,
    mediumPrice: ltp,
    lowPrice: Number(mw?.low ?? data.lowPrice) || 0,
    highPrice: Number(mw?.high ?? data.highPrice) || 0,
    executionType: closeExecutionType(data),
    symbol,
    positionId,
  };
}

function toastFromApi(dispatch, body, variant = "success") {
  const msg = body?.message ?? body?.Message;
  if (msg == null || String(msg).trim() === "") return;
  dispatch(showMessage({ message: String(msg), varient: variant }));
}

/** Mobile MT5 Done (error) page — always show a line; empty API text was silent before. */
function toastCloseFailure(dispatch, msg) {
  const text = String(msg || "").trim() || "Close position failed";
  dispatch(showMessage({ message: text, varient: "errorOrder" }));
}

// ── initialState ──────────────────────────────────────────────────────────────
const initialState = {
  // Plain arrays — new UI does NOT use Maps
  positionReportList          : [],
  filteredPositionReportList  : [],
  duplicatePositionReportList : [],

  positionReportLoading       : false,
  totalRecordPositionReport   : 0,
  currentPagePositionReport   : 1,
  pageSizePositionReport      : 10000,

  positionReportFilterValue   : {},
  positionReportSearchText    : "",

  closePositonSelected        : [],

  positionCloseConfirmDialog  : false,
  positionCloseData           : {},
  isPositionCloseLoading      : false,
  isClosePosition             : false,

  liveRateFromSocket          : {},
  positionDisplayNames        : [],

  isPositionGet               : false,
};

// ─────────────────────────────────────────────────────────────────────────────
// getPositionReportList
// ─────────────────────────────────────────────────────────────────────────────
export const getPositionReportList = createAsyncThunk(
  "positionReport/get",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(data.userId || session.userId);
      const roles   = session.roles;

      const {
        searchText, pageNo, pageSize, appendData,
        disptype, clientcode, subbrokercode, crtype,
        segment, valan, symbol, reporttype,
        exdate, isExDateSel,
      } = data;

      // Valan: react-select option shape { label, value: { id, startDate, endDate } }
      const valanObj = extractSelectOrPrimitive(valan); // null or { id, startDate, endDate }

      const reportType = reporttype === "client" ? 1 : reporttype === "script" ? 2 : 3;

      const queryUserId = data.userId
        ? safeParseInt(data.userId)
        : reporttype === "self" && extractSelectOrPrimitive(subbrokercode)
          ? safeParseInt(extractSelectOrPrimitive(subbrokercode))
          : userId;

      let clientId = null;
      if (reporttype === "client") {
        clientId = clientcode && Object.keys(clientcode).length > 0
          ? safeParseInt(extractSelectOrPrimitive(clientcode))
          : roles[0] === "User" ? userId : null;
      } else if (reporttype === "self") {
        clientId = roles[0] === "User" ? userId : null;
      }

      let startDate, endDate;
      if (crtype === "day") {
        startDate = formatApiDateMomentIso(new Date());
        endDate   = formatApiDateMomentIso(new Date());
      } else {
        startDate = valanObj?.startDate ? formatApiDateMomentIso(valanObj.startDate) : formatApiDateMomentIso(new Date());
        endDate   = valanObj?.endDate   ? formatApiDateMomentIso(valanObj.endDate)   : formatApiDateMomentIso(new Date());
      }

      const isClosed = disptype === "all";

      // History Module Step 6 — Closed Positions use positions/get-closed
      if (isClosed) {
        const closedPayload = {
          userId    : queryUserId,
          pageNo    : safeParseInt(pageNo)   || 1,
          pageSize  : safeParseInt(pageSize) || 10000,
          segmentId : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
          clientId,
          symbol    : extractSelectOrPrimitive(symbol) ?? null,
          searchText: searchText || null,
          startDate,
          endDate,
        };
        const { data: closedRes } = await api.post("positions/get-closed", closedPayload);
        if (closedRes.status == 200) {
          const records = (closedRes.data?.records || []).map((item) => ({
            ...item,
            type         : item.type || "",
            lots         : item.volumeLots,
            qty          : item.volumeQty,
            openPrice    : item.priceOpen,
            currentPrice : item.priceClose,
            profit       : item.realizedPnl,
            script       : item.displayName || item.symbol,
            displayName  : item.displayName || item.symbol,
            mtm          : item.realizedPnl,
            ltp          : item.priceClose,
            orderType    : item.type,
          }));
          return {
            data: { ...closedRes.data, records, totalRecords: closedRes.data?.totalRecords || records.length },
            appendData,
          };
        }
        const failMsg = closedRes?.message ?? closedRes?.Message;
        if (failMsg != null && String(failMsg).trim() !== "") {
          dispatch(showMessage({ message: String(failMsg) }));
        }
        return rejectWithValue(closedRes);
      }

      const payload = {
        createdBy    : userId,
        reportType,
        userId       : queryUserId,
        pageNo       : safeParseInt(pageNo)   || 1,
        pageSize     : safeParseInt(pageSize) || 10000,
        isDayWise    : crtype === "day",
        isOutStanding: true,
        segmentId    : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        startDate,
        endDate,
        valanId      : valanObj ? safeParseInt(valanObj.id) : 0,
        authorityId  : reporttype === "self" && extractSelectOrPrimitive(subbrokercode)
                         ? safeParseInt(extractSelectOrPrimitive(subbrokercode))
                         : roles[0] !== "Super Admin" && reporttype === "self"
                           ? userId : null,
        subBrokerId  : extractSelectOrPrimitive(subbrokercode) && reporttype !== "self"
                         ? safeParseInt(extractSelectOrPrimitive(subbrokercode)) : null,
        clientId,
        searchText   : searchText || null,
        exDate       : isExDateSel ? formatApiDateMomentIso(exdate) : null,
        symbol       : extractSelectOrPrimitive(symbol) ?? null,
      };

     

      const { data: res } = await api.post("position-report/get-open-positions", payload);

      if (res.status == 200) {
        // Do not toast on list fetch — it overwrites / clears the order-success toast
        // (generic API "Success" hits shouldSuppressGenericSuccessToast → showMessage=false).
        return { data: res.data, appendData };
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);

    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// exportPositionReport
// ─────────────────────────────────────────────────────────────────────────────
export const exportPositionReport = createAsyncThunk(
  "positionReport/export",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(data.userId || session.userId);

      const { disptype, clientcode, subbrokercode, crtype,
              segment, valan, symbol, reporttype, exdate, isExDateSel } = data;

      const valanObj = extractSelectOrPrimitive(valan);

      const payload = {
        userId,
        reportType   : reporttype === "client" ? 1 : reporttype === "script" ? 2 : 3,
        pageNo       : 1,
        pageSize     : 200000,
        isDayWise    : crtype === "day",
        isOutStanding: disptype !== "all",
        segmentId    : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        startDate    : crtype === "day" ? formatApiDateMomentIso(new Date()) : (valanObj?.startDate ? formatApiDateMomentIso(valanObj.startDate) : formatApiDateMomentIso(new Date())),
        endDate      : crtype === "day" ? formatApiDateMomentIso(new Date()) : (valanObj?.endDate   ? formatApiDateMomentIso(valanObj.endDate)   : formatApiDateMomentIso(new Date())),
        valanId      : valanObj ? safeParseInt(valanObj.id) : 0,
        subBrokerId  : extractSelectOrPrimitive(subbrokercode) ? safeParseInt(extractSelectOrPrimitive(subbrokercode)) : null,
        clientId     : extractSelectOrPrimitive(clientcode)    ? safeParseInt(extractSelectOrPrimitive(clientcode))    : null,
        symbol       : extractSelectOrPrimitive(symbol) ?? null,
        searchText   : data.searchText || null,
        exDate       : isExDateSel ? formatApiDateMomentIso(exdate) : null,
      };

      const { data: res } = await api.post("position-report/get-open-positions", payload);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        const rows = (res.data?.records || []).map(item => ({
          segment      : item.segment,
          client       : item.clientName,
          symbol       : item.symbol,
          script       : item.script,
          openTime     : item.openTime,
          type         : item.type,
          lots         : item.lots,
          qty          : item.qty,
          openPrice    : item.openPrice,
          currentPrice : item.currentPrice,
          profit       : item.profit,
          positionId   : item.positionId,
          authority    : item.authorityName,
        }));
        downloadCsvFromObjects(rows, "PositionReport");
        return { data: res.data };
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// closePosition
// ─────────────────────────────────────────────────────────────────────────────
export const closePosition = createAsyncThunk(
  "positionReport/closeOne",
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      if (!isSessionTradingAllowed(getSessionData() || session)) {
        toastCloseFailure(
          dispatch,
          "View-only login: you cannot place orders. Sign in with your trading password."
        );
        return rejectWithValue("View only");
      }

      const marketWatchList = getState()?.marketWatch?.marketWatchList;
      const payload = buildClosePositionPayload(data, session, marketWatchList);
      if (!payload.displayName) {
        toastCloseFailure(dispatch, "Script not found");
        return rejectWithValue("Script not found");
      }
      if (!payload.userId) {
        toastCloseFailure(dispatch, "Session expired. Please log in again.");
        return rejectWithValue("Session expired");
      }

      const { data: res } = await api.post("position-report/save-close-position", payload);

      if (res.status == 200) {
        /* successOrder → mobile GlobalReduxToast shows MT5 Done page (UI only). */
        toastFromApi(dispatch, res, "successOrder");
        dispatch(getMarketWatchList({ isRefresh: true }));
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      toastCloseFailure(dispatch, failMsg);
      return rejectWithValue(res);
    } catch (err) {
      toastCloseFailure(dispatch, legacyAxiosToastMessage(err) || axiosRejectPayload(err));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// saveAllClosePosition
// ─────────────────────────────────────────────────────────────────────────────
export const saveAllClosePosition = createAsyncThunk(
  "positionReport/closeAll",
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();

      const marketWatchList = getState()?.marketWatch?.marketWatchList;
      const finalArray = (data || []).map((item) =>
        buildClosePositionPayload(
          {
            ...item,
            lot: Math.abs(Number(item.netLot)),
            qty: Math.abs(Number(item.netQty)),
            price: item.ltp,
            ltp: item.ltp,
            marketType: "market",
            oldNetQty: item.netQty,
          },
          session,
          marketWatchList
        )
      );

      const { data: res } = await api.post("position-report/save-all-close-position", finalArray);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "successOrder");
        dispatch(getMarketWatchList({ isRefresh: true }));
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      toastCloseFailure(dispatch, failMsg);
      return rejectWithValue(res);
    } catch (err) {
      toastCloseFailure(dispatch, legacyAxiosToastMessage(err) || axiosRejectPayload(err));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// rollOverPosition
// ─────────────────────────────────────────────────────────────────────────────
export const rollOverPosition = createAsyncThunk(
  "positionReport/rollover",
  async (passedSelected, { dispatch, getState, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(session.userId);

      // Accept directly-passed array OR fall back to Redux store
      const { closePositonSelected } = getState().positionReport;
      const selected = (passedSelected && passedSelected.length > 0)
        ? passedSelected
        : closePositonSelected;

      if (!selected || selected.length === 0) {
        dispatch(showMessage({ message: "Please select at least one record" }));
        return rejectWithValue("Please select at least one record");
      }

      const finalArray = selected.map(item => ({
        userId      : item.userId,
        subBrokerId : null,
        script      : item.script,
        segmentId   : item.segmentId,
        segmentCode : item.segmentCode,
        symbol      : item.symbol,
        token       : item.token,
        exDate      : moment(item.exDate).format("YYYY-MM-DD"),
        date        : moment().format(),
        displayName : item.displayName,
        userIp      : session.userIp || "",
        valanId     : 0,
        deviceType  : 1,
        orderType   : item.netQty > 0 ? 1 : 2,
        tradeOption : 1,
        tradeType   : 1,
        isCheckHL   : true,
        lot         : Math.abs(item.netLot),
        qty         : Math.abs(item.netQty),
        lowPrice    : 0,
        mediumPrice : 0,
        highPrice   : 0,
        createdBy   : userId,
      }));

      const { data: res } = await api.post("position-report/save-roll-over-position", finalArray);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
export const positionReportSlice = createSlice({
  name: "positionReport",
  initialState,
  reducers: {
    setPositionReportSearchText    : (s, { payload }) => { s.positionReportSearchText   = payload; },
    setStartPagePositionReport     : (s, { payload }) => { s.currentPagePositionReport  = payload; },
    setTotalRecordPositionReport   : (s, { payload }) => { s.totalRecordPositionReport  = payload; },
    setPositionReportFilterValue   : (s, { payload }) => { s.positionReportFilterValue  = payload; },
    setClosePositonSelected        : (s, { payload }) => { s.closePositonSelected       = payload; },
    setIsPositionGet               : (s, { payload }) => { s.isPositionGet              = payload; },
    setPositionCloseConfirmDialog  : (s, { payload }) => { s.positionCloseConfirmDialog = payload; },
    setPositionCloseData           : (s, { payload }) => { s.positionCloseData          = payload; },
    setIsClosePosition             : (s, { payload }) => { s.isClosePosition            = payload; },
    setSocketData                  : (s, { payload }) => { s.liveRateFromSocket         = payload; },
    setPositionDisplayNames        : (s, { payload }) => { s.positionDisplayNames       = payload; },
    // Plain array setters — new UI uses arrays, NOT Maps
    setPositionReportList          : (s, { payload }) => { s.positionReportList          = payload || []; },
    setDuplicatePositionReportList : (s, { payload }) => { s.duplicatePositionReportList = payload || []; },
    setfilteredPositionReportList  : (s, { payload }) => { s.filteredPositionReportList  = payload || []; },
  },
  extraReducers: (builder) => {

    // ── getPositionReportList ────────────────────────────────────────────────
    builder
      .addCase(getPositionReportList.pending, (s) => {
        s.positionReportLoading = true;
      })
      .addCase(getPositionReportList.rejected, (s) => {
        s.positionReportList          = [];
        s.filteredPositionReportList  = [];
        s.duplicatePositionReportList = [];
        s.positionReportLoading       = false;
        s.totalRecordPositionReport   = 0;
        s.isPositionGet               = false;
      })
      .addCase(getPositionReportList.fulfilled, (s, { payload }) => {
        const records = payload.data?.records || [];
        const previousRows = s.filteredPositionReportList?.length
          ? s.filteredPositionReportList
          : s.positionReportList || [];

        let list;
        if (payload.appendData) {
          const existing = s.positionReportList || [];
          const newMapped = mapOpenPositionRecords(records, existing.length);
          list = [...existing, ...newMapped].map((r, idx) => ({
            ...r,
            isCheck: r.isCheck || false,
            rowId: idx + 1,
          }));
        } else {
          const mapped = mapOpenPositionRecords(records).map((r, idx) => ({
            ...r,
            isCheck: false,
            rowId: idx + 1,
          }));
          // Keep live MTM across auto-refresh so UI does not flash stale API profit/price.
          list = mergeLiveMtmIntoOpenPositions(mapped, previousRows);
        }

        s.positionReportList          = list;
        s.duplicatePositionReportList = list;
        s.filteredPositionReportList  = list;

        s.positionDisplayNames = list
          .filter(a => a.netQty !== 0)
          .map(a => a.displayName);

        s.totalRecordPositionReport = payload.data?.totalRecords || 0;
        s.positionReportLoading     = false;
        s.isPositionGet             = false;
      });

    // ── closePosition (single) ───────────────────────────────────────────────
    builder
      .addCase(closePosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(closePosition.rejected,  (s, { payload }) => {
        s.isPositionCloseLoading = false;
        // Stale UI row: server says already closed — refresh list to drop the row
        const msg = String(payload?.message || payload?.Message || "").toLowerCase();
        s.isClosePosition = msg.includes("position is close");
      })
      .addCase(closePosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── saveAllClosePosition ─────────────────────────────────────────────────
    builder
      .addCase(saveAllClosePosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(saveAllClosePosition.rejected,  (s, { payload }) => {
        s.isPositionCloseLoading = false;
        const msg = String(payload?.message || payload?.Message || "").toLowerCase();
        s.isClosePosition = msg.includes("position is close");
      })
      .addCase(saveAllClosePosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── rollOverPosition ─────────────────────────────────────────────────────
    builder
      .addCase(rollOverPosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(rollOverPosition.rejected,  (s) => { s.isPositionCloseLoading = false; s.isClosePosition = false; })
      .addCase(rollOverPosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── exportPositionReport ─────────────────────────────────────────────────
    builder
      .addCase(exportPositionReport.pending,   (s) => { s.positionReportLoading = true;  })
      .addCase(exportPositionReport.rejected,  (s) => { s.positionReportLoading = false; })
      .addCase(exportPositionReport.fulfilled, (s) => { s.positionReportLoading = false; });
  },
});

export const {
  setPositionReportSearchText,
  setStartPagePositionReport,
  setTotalRecordPositionReport,
  setPositionReportFilterValue,
  setClosePositonSelected,
  setIsPositionGet,
  setPositionCloseConfirmDialog,
  setPositionCloseData,
  setIsClosePosition,
  setSocketData,
  setPositionDisplayNames,
  setPositionReportList,
  setDuplicatePositionReportList,
  setfilteredPositionReportList,
} = positionReportSlice.actions;

export default positionReportSlice.reducer;